import { errorCopy } from '@/api/errorCopy';
/**
 * How should we help? — consent before the first evaluation (TMMAPP-0027).
 * Full variant stamps `reviewed: true`; compact variant edits switches without that stamp.
 * `layout="dialog"` (TMMAPP-0120) is the step inside a dialog whose title already names it: no
 * heading of its own, and the dialog's actions (a slot at the start, the cancel button, Save).
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from '@/components/SubmitButton';
import {
  automationApi,
  deterministicCatalogueItems,
  type AutomationCatalog,
} from '@/api/automation';
import type { PriceBook } from '@/api/entitlements';
import { entitlementsApi } from '@/api/entitlements';
import { patchMe } from '@/api/settings';
import { PriceBookBlock } from '@/features/settings/PriceBookBlock';
import { useMe, useMeRefetch } from '@/session/meHooks';

export interface ConsentStepProps {
  variant?: 'full' | 'compact';
  /** Full: the step's own title; a page that already has the `h1` passes `false` (TMMAPP-0072). */
  showTitle?: boolean | undefined;
  /** Initial catalogue (full/compact). Reloaded after writes. */
  catalog: AutomationCatalog;
  onCatalogChange?: ((catalog: AutomationCatalog) => void) | undefined;
  /** Called after a successful full Continue (reviewed stamped). */
  onCompleted?: (() => void) | undefined;
  /** Compact: called after a successful save of either switch. */
  onSaved?: (() => void) | undefined;
  /** Full: `dialog` renders the dialog's content and its actions (the cancel button, Save). */
  layout?: 'inline' | 'dialog';
  /** Dialog: the cancel button closes without saving; `changed` says a switch was moved. */
  onCancel?: ((changed: boolean) => void) | undefined;
  /** Dialog: the cancel button's label (default "Not now"). */
  cancelLabel?: string | undefined;
  /** Dialog: rendered at the start of the actions row (the "Don't show this again" box). */
  footerStart?: ReactNode;
  /** Dialog: the host's view of the step, so Escape can follow Not now and wait out a save. */
  onStatus?: ((status: { busy: boolean; changed: boolean }) => void) | undefined;
}

export function ConsentStep({
  variant = 'full',
  showTitle = true,
  catalog,
  onCatalogChange,
  onCompleted,
  onSaved,
  layout = 'inline',
  onCancel,
  cancelLabel,
  footerStart,
  onStatus,
}: ConsentStepProps) {
  const { t } = useTranslation();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const [autoOn, setAutoOn] = useState(() =>
    deterministicCatalogueItems(catalog).every((item) => item.enabled),
  );
  const [aiOn, setAiOn] = useState(() => (me.user.id !== null ? me.user.aiEnabled : false));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prices, setPrices] = useState<PriceBook | null>(null);
  const catalogSyncKey = `${catalog.reviewedAt ?? 'null'}:${deterministicCatalogueItems(catalog)
    .map((item) => `${item.key}:${item.enabled ? '1' : '0'}`)
    .join(',')}`;
  const [lastCatalogKey, setLastCatalogKey] = useState(catalogSyncKey);
  if (catalogSyncKey !== lastCatalogKey) {
    setLastCatalogKey(catalogSyncKey);
    setAutoOn(deterministicCatalogueItems(catalog).every((item) => item.enabled));
  }
  const meAi = me.user.id !== null ? me.user.aiEnabled : false;
  const [lastMeAi, setLastMeAi] = useState(meAi);
  if (meAi !== lastMeAi) {
    setLastMeAi(meAi);
    setAiOn(meAi);
  }

  useEffect(() => {
    let cancelled = false;
    void entitlementsApi
      .get()
      .then((ents) => {
        if (!cancelled) setPrices(ents.prices);
      })
      .catch(() => {
        if (!cancelled) setPrices(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const changed =
    autoOn !== deterministicCatalogueItems(catalog).every((item) => item.enabled) || aiOn !== meAi;

  useEffect(() => {
    onStatus?.({ busy, changed });
  }, [onStatus, busy, changed]);

  const showSeeded =
    me.user.id !== null && me.user.aiEnabledSource === 'DEFAULT' && aiOn && prices !== null;

  async function writeDeterministic(next: boolean): Promise<AutomationCatalog> {
    const items = deterministicCatalogueItems(catalog).map((item) => ({
      key: item.key,
      enabled: next,
    }));
    return automationApi.patch({ items });
  }

  async function writeAi(next: boolean): Promise<void> {
    if (me.user.id === null) return;
    if (me.user.aiEnabled === next) return;
    await patchMe({ aiEnabled: next });
    await refetchMe();
  }

  async function handleAutoChange(next: boolean) {
    if (busy) return;
    const previous = autoOn;
    setAutoOn(next);
    setError(null);
    if (variant === 'full') return;
    setBusy(true);
    try {
      const updated = await writeDeterministic(next);
      onCatalogChange?.(updated);
      onSaved?.();
    } catch (err) {
      setAutoOn(previous);
      setError(errorCopy(t, err, 'automation.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  async function handleAiChange(next: boolean) {
    if (busy) return;
    const previous = aiOn;
    setAiOn(next);
    setError(null);
    if (variant === 'full') return;
    setBusy(true);
    try {
      await writeAi(next);
      onSaved?.();
    } catch (err) {
      setAiOn(previous);
      setError(errorCopy(t, err, 'automation.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  async function handleContinue() {
    if (busy || me.user.id === null) return;
    setBusy(true);
    setError(null);
    try {
      const detItems = deterministicCatalogueItems(catalog);
      const autoChanged = detItems.some((item) => item.enabled !== autoOn);
      const items = autoChanged
        ? detItems.map((item) => ({ key: item.key, enabled: autoOn }))
        : undefined;
      // Preferences before the stamp: AI first (master), then catalogue + reviewed together.
      if (aiOn !== me.user.aiEnabled) {
        await patchMe({ aiEnabled: aiOn });
      }
      const updated = await automationApi.patch({
        ...(items !== undefined ? { items } : {}),
        reviewed: true,
      });
      await refetchMe();
      onCatalogChange?.(updated);
      onCompleted?.();
    } catch (err) {
      setError(errorCopy(t, err, 'automation.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  const dialog = layout === 'dialog' && variant === 'full';
  const content = (
    <Stack spacing={2} sx={{ maxWidth: 560 }}>
      {variant === 'full' && (
        <>
          {showTitle && !dialog && <Typography variant="h2">{t('consent.title')}</Typography>}
          <Typography variant="body1">{t('consent.body')}</Typography>
        </>
      )}
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={autoOn}
              disabled={busy}
              onChange={(event) => void handleAutoChange(event.target.checked)}
              slotProps={{ input: { 'aria-label': t('consent.autoLabel') } }}
            />
          }
          label={t('consent.autoLabel')}
        />
        <Typography variant="body2" color="text.secondary">
          {t('consent.autoBody')}
        </Typography>
      </Box>
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={aiOn}
              disabled={busy || me.user.id === null}
              onChange={(event) => void handleAiChange(event.target.checked)}
              slotProps={{ input: { 'aria-label': t('consent.aiLabel') } }}
            />
          }
          label={t('consent.aiLabel')}
        />
        <Typography variant="body2" color="text.secondary">
          {t('consent.aiBody')}
        </Typography>
        {showSeeded && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('consent.aiSeeded')}
            </Typography>
            <PriceBookBlock prices={prices!} tier={me.tier} />
          </Box>
        )}
      </Box>
      {error !== null && <Alert severity="error">{error}</Alert>}
      {variant === 'full' && !dialog && (
        <Box>
          <SubmitButton loading={busy} onClick={() => void handleContinue()}>
            {t('consent.continue')}
          </SubmitButton>
        </Box>
      )}
    </Stack>
  );

  if (!dialog) return content;
  return (
    <>
      <DialogContent>{content}</DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {footerStart !== undefined && (
          <Box sx={{ marginInlineEnd: 'auto', minWidth: 0 }}>{footerStart}</Box>
        )}
        <Button disabled={busy} onClick={() => onCancel?.(changed)}>
          {cancelLabel ?? t('consent.notNow')}
        </Button>
        <SubmitButton loading={busy} onClick={() => void handleContinue()}>
          {t('consent.save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
